import React, { forwardRef } from 'react';

export type NumericInputMode = 'integer' | 'decimal';

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'pattern'> {
  /**
   * 'integer' -> kun heltall (tastatur uten komma/punktum)
   * 'decimal' -> desimaltall (tastatur med komma/punktum) [default]
   */
  mode?: NumericInputMode;
}

/**
 * NumericInput — drop-in erstatning for <input type="number"> til antalls-/kvantitetsfelter
 * pa nettbrett.
 *
 * Problem som loeses:
 * - type="number" gir inkonsistent Enter-oppforsel mellom iOS/Android
 * - Noen Android-tastaturer mangler desimaltegn nar man bruker type="number"
 * - type="number" kan feile med strekkodelesere som sender kontrolltegn
 * - Brukere kan taste ugyldige tegn (bokstaver) som parent-komponenten ma filtrere selv
 * - Norske brukere taster komma, men backend forventer ofte punktum
 *
 * Bruker type="text" + inputMode for riktig virtuelt tastatur. Enter og scanner-input
 * fungerer konsistent.
 *
 * Automatisk input-filtrering:
 * - 'integer': fjerner alle tegn som ikke er 0-9
 * - 'decimal': fjerner alle tegn som ikke er 0-9 eller desimaltegn
 *   - Komma normaliseres til punktum automatisk (norsk tastatur -> backend-format)
 *   - Kun ett desimaltegn tillates (ekstra droppes)
 *
 * Tablet-vennlige defaults:
 *   type="text"                 -> konsistent Enter-oppforsel
 *   inputMode="decimal"/numeric -> numerisk tastatur
 *   pattern                     -> browser-hint for soft-keyboard
 *   enterKeyHint="done"         -> "Ferdig"-knapp som lukker tastaturet
 *
 * Paavirker ikke:
 * - Fysiske strekkodelesere
 * - Global barcode-hook
 *
 * MERK: Verdivalidering utover tegn-filtrering (min/max/steg) ma handteres i
 * onChange/onBlur — type="text" utforer ingen native numerisk validering.
 */

const filterIntegerInput = (raw: string): string => {
  return raw.replace(/[^0-9]/g, '');
};

const filterDecimalInput = (raw: string): string => {
  // Normaliser komma til punktum forst
  const normalized = raw.replace(/,/g, '.');
  // Fjern alle tegn som ikke er 0-9 eller punktum
  const cleaned = normalized.replace(/[^0-9.]/g, '');
  // Tillat kun ett punktum — behold forste, dropp resten
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  function NumericInput({ mode = 'decimal', onChange, ...props }, ref) {
    const inputMode = mode === 'integer' ? 'numeric' : 'decimal';
    const pattern = mode === 'integer' ? '[0-9]*' : '[0-9]*[.,]?[0-9]*';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const filtered = mode === 'integer' ? filterIntegerInput(raw) : filterDecimalInput(raw);

      // Kun oppdater target.value hvis vi faktisk endret noe — hindrer unodig re-render
      // og bevarer caret-posisjon i normale tilfeller.
      if (filtered !== raw) {
        e.target.value = filtered;
      }

      onChange?.(e);
    };

    return (
      <input
        ref={ref}
        type="text"
        inputMode={inputMode}
        pattern={pattern}
        enterKeyHint="done"
        autoComplete="off"
        onChange={handleChange}
        {...props}
      />
    );
  }
);
